import React, { useState, useCallback, useRef, useEffect } from "react";
import "./CatalogModule.scss";
import { ProductUI } from "@/shared/types";
import { CategoriesList } from "@/shared/components/CategoriesList";
import { ProductsGrid } from "@/shared/components/ProductsGrid";
import { ProductModal } from "@/shared/components/ProductModal";
import { ThemeLoader } from "@/shared/components/ThemeLoader";
import { useCatalog } from "@/shared/hooks/useCatalog";
import { useCart } from "@/app/providers/CartProvider";
import { useCity } from "@/app/providers/CityProvider";

const PULL_THRESHOLD = 100;
const MAX_PULL = 200;

export const CatalogModule: React.FC = () => {
  const {
    selectedCity,
    isLoading: isCityLoading,
    error: cityError,
    refreshCities,
  } = useCity();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<ProductUI | null>(
    null
  );
  const [searchInput, setSearchInput] = useState<string>("");

  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const observerTarget = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef<number>(0);
  const currentY = useRef<number>(0);
  const isFirstSearchEffect = useRef(true);

  const {
    categories,
    products,
    loading,
    pagination,
    loadMoreProducts,
    searchProducts,
    refreshCatalog,
  } = useCatalog(selectedCategory, { pageSize: 6 }, selectedCity?.id, selectedCity?.outletId ?? undefined);

  const { addToCart } = useCart();

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isPulling) return;

      currentY.current = e.touches[0].clientY;
      const diff = currentY.current - startY.current;

      if (diff > 0) {
        e.preventDefault();
        const distance = Math.min(diff * 0.5, MAX_PULL);
        setPullDistance(distance);
      }
    },
    [isPulling]
  );

  const handleTouchEnd = useCallback(async () => {
    if (!isPulling) return;

    setIsPulling(false);

    if (pullDistance >= PULL_THRESHOLD && !loading.products) {
      setIsRefreshing(true);
      try {
        await refreshCatalog();
      } finally {
        setIsRefreshing(false);
      }
    }

    setPullDistance(0);
  }, [isPulling, pullDistance, loading.products, refreshCatalog]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.clientY;
      setIsPulling(true);
    }
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPulling) return;

      currentY.current = e.clientY;
      const diff = currentY.current - startY.current;

      if (diff > 0) {
        e.preventDefault();
        const distance = Math.min(diff * 0.5, MAX_PULL);
        setPullDistance(distance);
      }
    },
    [isPulling]
  );

  const handleMouseUp = useCallback(async () => {
    if (!isPulling) return;

    setIsPulling(false);

    if (pullDistance >= PULL_THRESHOLD && !loading.products) {
      setIsRefreshing(true);
      try {
        await refreshCatalog();
      } finally {
        setIsRefreshing(false);
      }
    }
    setPullDistance(0);
  }, [isPulling, pullDistance, loading.products, refreshCatalog]);

  useEffect(() => {
    return () => {
      setIsPulling(false);
      setPullDistance(0);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          pagination.hasNextPage &&
          !loading.moreProducts &&
          !loading.products
        ) {
          loadMoreProducts();
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => observer.disconnect();
  }, [
    pagination.hasNextPage,
    loading.moreProducts,
    loading.products,
    loadMoreProducts,
  ]);

  // Debounce для поиска (не дергаем пустой запрос при первом монтировании)
  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      if (!searchInput.trim()) return;
    }

    const timer = setTimeout(() => {
      const query = searchInput.trim();
      // Поиск по всему каталогу: иначе фильтр категории даёт «пустой» результат
      if (query) {
        setSelectedCategory(null);
      }
      searchProducts(query);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, searchProducts]);

  const handleCategorySelect = useCallback(
    (categoryId: string) => {
      setSelectedCategory(categoryId === "all" ? null : categoryId);
      setSearchInput("");
      searchProducts("");
    },
    [searchProducts]
  );

  const handleProductClick = useCallback((product: ProductUI) => {
    setSelectedProduct(product);
  }, []);

  const handleAddToCart = useCallback(
    (product: ProductUI, quantity: number) => {
      addToCart(product, quantity);
      setSelectedProduct(null);
    },
    [addToCart]
  );

  const handleRefresh = useCallback(async () => {
    setSearchInput("");
    await refreshCatalog();
  }, [refreshCatalog]);

  if (isCityLoading) {
    return (
      <div className="catalog-module__loader">
        <div className="catalog-module__spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  if (!selectedCity?.id) {
    return (
      <div className="catalog-module">
        <div className="catalog-module__empty">
          <p>{cityError ?? "Нет доступных торговых объектов"}</p>
          <p className="catalog-module__empty-hint">Каталог временно недоступен</p>
          <button type="button" onClick={() => void refreshCities()}>
            Обновить
          </button>
        </div>
      </div>
    );
  }

  const isInitialCatalogLoading =
    (loading.categories || loading.products) &&
    categories.length === 0 &&
    products.length === 0 &&
    !loading.categoriesError &&
    !loading.productsError;

  if (isInitialCatalogLoading) {
    return (
      <div className="catalog-module">
        <ThemeLoader />
      </div>
    );
  }

  const renderProductCounter = () => {
    if (pagination.totalItems === 0) return null;
    const start = (pagination.currentPage - 1) * 6 + 1;
    const end = Math.min(pagination.currentPage * 6, pagination.totalItems);

    return (
      <div className="catalog-module__counter">
        Показано {start}-{end} из {pagination.totalItems} товаров
      </div>
    );
  };

  const renderPullIndicator = () => {
    const isReady = pullDistance >= PULL_THRESHOLD;
    const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
    const rotation = progress * 360;

    return (
      <div
        className="catalog-module__pull-indicator"
        style={{
          height: pullDistance,
          opacity: pullDistance > 0 ? 1 : 0,
        }}
      >
        <div className="catalog-module__pull-content">
          {isRefreshing ? (
            <>
              <div className="catalog-module__spinner catalog-module__spinner--small"></div>
              <span>Обновление...</span>
            </>
          ) : isReady ? (
            <>
              <div
                className="catalog-module__pull-arrow catalog-module__pull-arrow--ready"
                style={{ transform: `rotate(${rotation}deg)` }}
              >
                ↓
              </div>
              <span>Отпустите для обновления</span>
            </>
          ) : (
            <>
              <div
                className="catalog-module__pull-arrow"
                style={{ transform: `rotate(${rotation}deg)` }}
              >
                ↓
              </div>
              <span>Тяните вниз для обновления</span>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="catalog-module"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={() => {
        setIsPulling(false);
        setPullDistance(0);
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        setIsPulling(false);
        setPullDistance(0);
      }}
      style={{
        overscrollBehavior: "contain",
        touchAction: isPulling ? "none" : "auto",
      }}
    >
      {renderPullIndicator()}

      <div className="catalog-module__header">
        <span className="catalog-module__city-label">
          Ваш город: <b>{selectedCity.name}</b>
        </span>
      </div>

      <div className="catalog-module__search">
        <input
          type="text"
          placeholder="Поиск товаров..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="catalog-module__search-input"
        />
      </div>

      <CategoriesList
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={handleCategorySelect}
        isLoading={loading.categories}
      />

      {!loading.products && renderProductCounter()}

      <ProductsGrid
        products={products}
        onProductClick={handleProductClick}
        isLoading={loading.products}
      />

      {loading.moreProducts && (
        <div className="catalog-module__loading-more">
          <div className="catalog-module__spinner"></div>
          <span>Загрузка товаров...</span>
        </div>
      )}

      <div ref={observerTarget} className="catalog-module__observer" />

      {pagination.hasNextPage &&
        !loading.moreProducts &&
        products.length > 0 && (
          <button
            className="catalog-module__load-more"
            onClick={loadMoreProducts}
          >
            Загрузить еще товары
          </button>
        )}

      {(loading.categoriesError || loading.productsError) && (
        <div className="catalog-module__error">
          <p>{loading.categoriesError || loading.productsError}</p>
          <button onClick={handleRefresh}>Попробовать снова</button>
        </div>
      )}

      <ProductModal
        product={selectedProduct}
        onClose={() => setSelectedProduct(null)}
        onAddToCart={handleAddToCart}
      />
    </div>
  );
};
